'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '../../ui/carousel-controls';
import { useMemo } from 'react';

export function ProductImages({
  priorityFirstImage = false,
}: { priorityFirstImage?: boolean } = {}) {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const options = {
    loop: true,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { api, selectedIndex } = carouselContext;

  const showOnlyVariantImages =
    templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false;
  const images = useMemo(
    () => filerImages(showOnlyVariantImages, product, selectedVariant),
    [showOnlyVariantImages, product, selectedVariant],
  );

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[12px] bg-[#F9FAFB] p-2 sm:gap-5 lg:gap-6 lg:p-4">
      <div className="relative w-full">
        <CarouselControls
          className={{
            main: 'absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-between px-2 sm:px-3 lg:px-4',
          }}
        />
        <Carousel
          options={options}
          className={{
            container: 'w-full',
            main: 'w-full',
            wrapper: 'w-full',
            slide: 'w-full overflow-hidden rounded-[12px] bg-white',
          }}
          slides={images.map((image, index) => (
            <Image
              key={index}
              src={image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(image, product?.name)}
              height={0}
              width={0}
              className="aspect-square w-full object-contain object-center p-2 lg:p-3"
              sizes={
                priorityFirstImage
                  ? '(min-width: 1440px) 688px, (min-width: 1024px) calc(52.6vw - 70px), (min-width: 768px) calc(52.6vw - 50px), calc(100vw - 56px)'
                  : '100vw'
              }
              loading={priorityFirstImage && index === 0 ? 'eager' : 'lazy'}
              fetchPriority={priorityFirstImage && index === 0 ? 'high' : 'low'}
            />
          ))}
        />
      </div>

      <div className="hidden w-full grid-cols-5 gap-4 sm:grid">
        {images.slice(0, 5).map((image, index) => (
          <button
            key={index}
            type="button"
            aria-label={`${product.name} – ${index + 1}`}
            aria-pressed={selectedIndex === index}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              'aspect-square w-full rounded-[12px] bg-white p-1 opacity-50 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary lg:p-3',
              selectedIndex === index && 'opacity-100',
              selectedIndex !== index && 'hover:opacity-75',
            )}
          >
            <Image
              src={image?.url ?? '/images/placeholder.png'}
              alt=""
              height={0}
              width={0}
              className="aspect-square w-full object-contain object-center"
              sizes="(min-width: 1440px) 112px, (min-width: 768px) 10vw, 18vw"
              loading="lazy"
              fetchPriority="low"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images =
    showOnlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
